import {
  ChangeDetectionStrategy,
  Component,
  effect,
  type ElementRef,
  forwardRef,
  inject,
  input,
  type OnDestroy,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  type CreateTokenCardData,
  type StripeCardElement,
  type StripeError,
  type Token,
} from '@stripe/stripe-js';
import { merge, type Observable, ReplaySubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { StripeService } from '../stripe.service';
import { StripeCardEvents } from './card-events';

const accountAccessDeniedPattern = /does not have access to account/i;

@Component({
  selector: 'pt-card-input',
  templateUrl: './card-input.html',
  styleUrls: ['./card-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CardInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CardInputComponent implements ControlValueAccessor, OnDestroy {
  private _stripe = inject(StripeService);
  private _onDestroy$: Subject<void> = new Subject();
  private _tokenError$: ReplaySubject<StripeError | undefined> =
    new ReplaySubject(1);
  private _card?: StripeCardElement;
  private _mountVersion = 0;
  errorMessage$: Observable<string | undefined>;
  token$: Subject<Token> = new Subject<Token>();
  card$: Subject<StripeCardElement> = new ReplaySubject<StripeCardElement>(1);
  events: StripeCardEvents;
  readonly initError = signal(false);
  readonly connectedAccountId = input.required<string>();
  readonly accountErrorMessage = input<string>();
  readonly container =
    viewChild.required<ElementRef<HTMLElement>>('stripeContainer');

  constructor() {
    this.events = new StripeCardEvents(this.card$);
    this.errorMessage$ = merge(this._tokenError$, this.events.onError$).pipe(
      map((error) => this._getErrorMessage(error))
    );
    effect(() => {
      const connectedAccountId = this.connectedAccountId();
      untracked(() => void this._mountCard(connectedAccountId));
    });
  }

  /**
   * A Stripe error caused by the connected account rather than the card, such
   * as a disconnected or changed practice account. The card holder cannot fix
   * these, so callers show an account message instead of Stripe's text.
   */
  static isAccountError(error: StripeError): boolean {
    return (
      error.code === 'account_invalid' ||
      (!!error.message && accountAccessDeniedPattern.test(error.message))
    );
  }

  ngOnDestroy(): void {
    this._mountVersion++;
    this._destroyCard();
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  /**
   * A token is not something that persists anywhere so it would
   * never be available to call writeValue in the first place.
   */
  writeValue(_token: Token): void {
    // Do nothing
  }

  registerOnChange(callback: (value: Token) => unknown): void {
    this.token$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((value) => callback(value));
  }

  registerOnTouched(callback: () => void): void {
    this.events.onBlur$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => callback());
  }

  async update(data: CreateTokenCardData = {}): Promise<void> {
    const connectedAccountId = this.connectedAccountId();
    const stripe = await this._stripe.forAccount(connectedAccountId);
    if (!stripe) {
      // eslint-disable-next-line no-console
      console.error('Unable to load stripe');
      return;
    }
    const card = this._card;
    if (!card || connectedAccountId !== this.connectedAccountId()) {
      return;
    }
    const { token, error } = await stripe.createToken(card, data);
    if (connectedAccountId !== this.connectedAccountId()) {
      return;
    }
    if (error) {
      this._tokenError$.next(error);
    }
    if (token) {
      this.token$.next(token);
    }
  }

  private _getErrorMessage(error?: StripeError): string | undefined {
    if (!error) {
      return;
    }
    const accountErrorMessage = this.accountErrorMessage();
    if (accountErrorMessage && CardInputComponent.isAccountError(error)) {
      return accountErrorMessage;
    }
    return error.message || 'Invalid Payment Token';
  }

  private async _mountCard(connectedAccountId: string): Promise<void> {
    const mountVersion = ++this._mountVersion;
    this._destroyCard();

    try {
      const stripe = await this._stripe.forAccount(connectedAccountId);
      if (mountVersion !== this._mountVersion) {
        return;
      }
      if (!stripe) {
        this._showInitError();
        return;
      }
      this.initError.set(false);
      const card = stripe.elements().create('card', {
        hidePostalCode: true,
      });
      card.mount(this.container().nativeElement);
      this._card = card;
      this.card$.next(card);
    } catch (error) {
      // eslint-disable-next-line no-console -- records why the card field failed to load, which the user only sees as "Unable to load Stripe"
      console.error('Unable to mount the Stripe card element', error);
      if (mountVersion !== this._mountVersion) {
        return;
      }
      this._showInitError();
    }
  }

  private _showInitError(): void {
    this.initError.set(true);
  }

  private _destroyCard(): void {
    const card = this._card;
    this._card = undefined;
    card?.unmount();
    card?.destroy();
  }
}
